<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-6xl mx-auto px-4">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-6">
        <div>
          <button @click="$router.back()" class="flex items-center gap-2 text-slate-500 hover:text-orange-600 transition-colors mb-4 self-start">
            <v-icon small color="inherit">mdi-arrow-left</v-icon>
            <span class="text-[10px] font-black uppercase tracking-widest">Voltar</span>
          </button>
          <h1 class="sci-page-header-title text-2xl font-black text-slate-800 uppercase tracking-tight">{{ id ? 'Editar Nota Fiscal' : 'Lançar Nota Fiscal' }}</h1>
          <p class="text-xs text-slate-500 font-bold mt-1 uppercase tracking-wider opacity-60">Gestão de Peças e Serviços</p>
        </div>
      </div>

      <v-form ref="form" v-model="valid" lazy-validation class="space-y-8 pb-12">
        
        <!-- SEÇÃO 1: IDENTIFICAÇÃO -->
        <section class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xl shadow-slate-200/50">
          <div class="px-8 py-5 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-file-document-outline</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">IDENTIFICAÇÃO DA NOTA</h2>
          </div>
          
          <div class="p-10">
            <v-row>
              <!-- PREFEITURA -->
              <v-col cols="12" sm="12" md="6">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Prefeitura</label>
                <v-autocomplete
                  v-model="data.prefecture_id"
                  :items="prefectures"
                  item-value="prefecture_id"
                  item-text="prefecture_social_name"
                  @change="[loadProviders(), loadSec(), getVehiclesByPrefecture()]"
                  :readonly="formDisable"
                  placeholder="SELECIONE A PREFEITURA"
                  solo flat dense hide-details
                  class="custom-select"
                />
              </v-col>

              <!-- PARTICIPANTE -->
              <v-col cols="12" sm="12" md="6">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Participante (Fornecedor)</label>
                <v-autocomplete
                  v-model="data.provider_id"
                  :items="providers"
                  item-value="provider_id"
                  item-text="provider_name"
                  @change="[loadProvidersItem()]"
                  placeholder="SELECIONE O PARTICIPANTE"
                  solo flat dense hide-details
                  class="custom-select"
                />
              </v-col>

              <!-- VEÍCULO -->
              <v-col cols="12" sm="12" md="6">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Veículo</label>
                <v-autocomplete
                  v-model="data.vehicle_id"
                  :items="vehicles"
                  item-value="vehicle_id"
                  placeholder="SELECIONE O VEÍCULO"
                  solo flat dense hide-details
                  class="custom-select"
                >
                  <template v-slot:selection="{ item }">
                    {{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}
                  </template>
                  <template v-slot:item="{ item }">
                    {{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- NÚMERO NF -->
              <v-col cols="12" sm="12" md="3">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Número NF</label>
                <v-text-field
                  v-model="data.nf_number"
                  v-mask-number
                  placeholder="000"
                  solo flat dense hide-details
                  class="custom-select"
                />
              </v-col>

              <!-- DATA EMISSÃO -->
              <v-col cols="12" sm="12" md="3">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Data Emissão</label>
                <v-text-field
                  v-model="data.nf_issue_date"
                  v-mask-date.br
                  placeholder="DD/MM/AAAA"
                  solo flat dense hide-details
                  class="custom-select"
                />
              </v-col>

              <!-- RESPONSÁVEL -->
              <v-col cols="12">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Nome do Responsável</label>
                <v-text-field
                  v-model="data.nf_name"
                  placeholder="DIGITE O NOME DO RESPONSÁVEL"
                  solo flat dense hide-details
                  class="custom-select"
                />
              </v-col>

              <!-- UNIDADE ORÇAMENTÁRIA -->
              <v-col cols="12" sm="12" md="6">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Unidade Orçamentária</label>
                <v-autocomplete
                  v-model="data.nf_sec_id"
                  :items="prefectures_sec"
                  item-value="sec_id"
                  item-text="sec_name"
                  placeholder="SELECIONE A UNIDADE"
                  solo flat dense hide-details
                  class="custom-select"
                  append-icon="mdi-plus-circle-outline"
                  @click:append="bidding_id ? $router.push(`/parts-and-services/biddings-manager/edit/${bidding_id}?tab=1`) : null"
                />
              </v-col>

              <!-- U.O. DESTINATÁRIO -->
              <v-col cols="12" sm="12" md="6">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">U.O. Destinatário</label>
                <v-autocomplete
                  v-model="data.nf_sec_recipient_id"
                  :items="prefectures_sec"
                  item-value="sec_id"
                  item-text="sec_name"
                  placeholder="SELECIONE A UNIDADE"
                  solo flat dense hide-details
                  class="custom-select"
                  append-icon="mdi-plus-circle-outline"
                  @click:append="bidding_id ? $router.push(`/parts-and-services/biddings-manager/edit/${bidding_id}?tab=1`) : null"
                />
              </v-col>
            </v-row>
          </div>
        </section>

        <!-- SEÇÃO 2: ITENS E SERVIÇOS -->
        <section class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xl shadow-slate-200/50">
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
            <div class="flex items-center gap-3">
              <v-icon color="orange" small>mdi-format-list-checks</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">ITENS E SERVIÇOS</h2>
            </div>
            <div class="flex gap-4">
              <button 
                type="button" 
                @click="addFieldParts"
                class="bg-orange-500 hover:bg-orange-600 text-white rounded-xl px-4 py-2.5 transition-all font-black text-[10px] uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-orange-500/20"
              >
                <v-icon small color="white">mdi-plus</v-icon>
                ADD PEÇA
              </button>
              <button 
                type="button" 
                @click="addFieldServices"
                class="bg-slate-700 hover:bg-slate-800 text-white rounded-xl px-4 py-2.5 transition-all font-black text-[10px] uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-slate-700/20"
              >
                <v-icon small color="white">mdi-hammer-wrench</v-icon>
                ADD SERVIÇO
              </button>
            </div>
          </div>
          
          <div class="p-8">
            <div v-if="itens.length === 0" class="py-20 border-2 border-dashed border-slate-100 rounded-2xl flex flex-col items-center justify-center text-slate-300 bg-slate-50/50">
              <v-icon size="64" color="slate-200">mdi-playlist-plus</v-icon>
              <p class="mt-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Nenhum item ou serviço adicionado</p>
            </div>

            <div class="space-y-4">
              <div v-for="(item, i) in itens" :key="i" class="p-6 bg-slate-50/80 rounded-2xl border border-slate-200 transition-all hover:bg-white hover:shadow-lg hover:shadow-slate-200/50 relative group">
                <v-row align="center" no-gutters v-if="item.isServico">
                  <v-col cols="12" md="8" class="pr-4">
                    <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Descrição do Serviço</label>
                    <v-text-field
                      v-model="item.servico"
                      placeholder="DESCREVA O SERVIÇO REALIZADO"
                      solo flat dense hide-details
                      class="custom-select-small uppercase"
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="pr-4 mt-4 md:mt-0">
                    <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Preço do Serviço</label>
                    <v-text-field
                      v-model="item.preco"
                      v-mask-decimal.br="2"
                      @change="onChangeTotal()"
                      placeholder="0,00"
                      prefix="R$"
                      solo flat dense hide-details
                      class="custom-select-small"
                    />
                  </v-col>
                  <v-col cols="12" md="1" class="mt-4 md:mt-0 flex justify-end items-end pt-5">
                    <v-btn icon color="rose" @click="removeField(i)" class="bg-rose-50 hover:bg-rose-100 transition-colors">
                      <v-icon small>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row align="center" no-gutters v-else>
                  <v-col cols="12" md="4" class="pr-4">
                    <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Peça / Produto</label>
                    <v-autocomplete
                      v-model="item.peca_id"
                      :items="providersItem"
                      item-value="item_id"
                      placeholder="BUSCAR PEÇA"
                      solo flat dense hide-details
                      class="custom-select-small uppercase"
                    >
                      <template v-slot:item="{ item }">
                        <span class="text-[10px] font-black uppercase tracking-tight">{{ `${item.name} - ${item.marca} - ${item.modelo}` }}</span>
                      </template>
                      <template v-slot:selection="{ item }">
                        <span class="text-[10px] font-black uppercase tracking-tight">{{ `${item.name} - ${item.marca}` }}</span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="2" class="pr-4 mt-4 md:mt-0">
                    <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">P. Unitário</label>
                    <v-text-field
                      v-model="item.preco"
                      v-mask-decimal.br="2"
                      @change="onChangeParts(i)"
                      placeholder="0,00"
                      prefix="R$"
                      solo flat dense hide-details
                      class="custom-select-small"
                    />
                  </v-col>
                  <v-col cols="12" md="2" class="pr-4 mt-4 md:mt-0">
                    <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Quantidade</label>
                    <v-text-field
                      v-model="item.quantidade"
                      v-mask-number
                      @change="[onChangeParts(i), onChangeTotal()]"
                      placeholder="0"
                      solo flat dense hide-details
                      class="custom-select-small text-center"
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="pr-4 mt-4 md:mt-0">
                    <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Total do Item</label>
                    <v-text-field
                      v-model="item.total"
                      v-mask-decimal.br="2"
                      readonly
                      placeholder="0,00"
                      prefix="R$"
                      solo flat dense hide-details
                      class="custom-select-small-readonly"
                    />
                  </v-col>
                  <v-col cols="12" md="1" class="mt-4 md:mt-0 flex justify-end items-end pt-5">
                    <v-btn icon color="rose" @click="removeField(i)" class="bg-rose-50 hover:bg-rose-100 transition-colors">
                      <v-icon small>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </div>
          </div>
        </section>

        <!-- SEÇÃO 3: TOTAIS -->
        <section class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xl shadow-slate-200/50">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-cash-register</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">RESUMO FINANCEIRO</h2>
          </div>
          
          <div class="p-8">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div class="p-8 bg-slate-50 rounded-2xl border border-slate-200 shadow-inner">
                <label class="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 block">Desconto Aplicado</label>
                <v-text-field
                  v-model="data.nf_discount"
                  v-mask-decimal.br="2"
                  @change="onChangeTotal()"
                  placeholder="0,00"
                  prefix="R$"
                  outlined dense hide-details
                  class="bg-white rounded-xl shadow-lg font-black text-2xl text-rose-500"
                />
              </div>

              <div class="p-8 bg-orange-50 rounded-2xl border border-orange-100 shadow-inner">
                <label class="text-[11px] font-black text-orange-400 uppercase tracking-widest mb-4 block">Valor Total da Nota</label>
                <v-text-field
                  v-model="data.nf_total"
                  v-mask-decimal.br="2"
                  readonly
                  placeholder="0,00"
                  prefix="R$"
                  outlined dense hide-details
                  class="bg-white rounded-xl shadow-lg font-black text-3xl text-orange-600"
                />
              </div>
            </div>

            <!-- FOOTER -->
            <div class="mt-12 bg-slate-50 px-8 py-4 border-t border-slate-100 flex justify-end items-center gap-4 -mx-8 -mb-8">
              <button 
                type="button"
                @click="$router.push('/parts-and-services/nf')"
                style="background: none; border: none; cursor: pointer; white-space: nowrap;"
                class="text-slate-400 hover:text-slate-600 font-bold text-[11px] uppercase tracking-widest transition-colors"
              >
                CANCELAR
              </button>
              <button
                type="button"
                @click="validate"
                :disabled="loader"
                style="background-color: #f37021; color: white; display: inline-flex; align-items: center; gap: 8px; border-radius: 8px; padding: 0 24px; font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; border: none; cursor: pointer; height: 42px; width: auto; flex-shrink: 0;"
              >
                <v-icon v-if="loader" small class="animate-spin" color="white">mdi-loading</v-icon>
                <v-icon v-else small color="white">mdi-check</v-icon>
                <span>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR LANÇAMENTO' }}</span>
              </button>
            </div>          </div>
        </section>
      </v-form>

      <!-- NOTIFICAÇÕES -->
      <v-snackbar
        v-model="snack.show"
        :color="snack.color"
        timeout="4000"
        rounded="xl"
        elevation="24"
        class="mb-8"
      >
        <div class="flex items-center gap-3">
          <v-icon color="white">{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
          <span class="text-xs font-black uppercase tracking-widest">{{ snack.message }}</span>
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn dark icon v-bind="attrs" @click="snack.show = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      nf_id: null,
      nf_number: "",
      prefecture_id: null,
      provider_id: null,
      vehicle_id: null,
      nf_issue_date: "",
      nf_name: "",
      nf_sec_id: null,
      nf_sec_recipient_id: null,
      nf_item: null,
      nf_discount: "",
      nf_total: "",
    },
    prefectures: [],
    providers: [],
    providersItem: [],
    itens: [],
    vehicles: [],
    formDisable: false,
    prefectures_sec: [],
    bidding_id: null,
  }),
  created() {
    this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`/parts-and-services/nf/${this.id}`).then((r) => {
        this.data = r.data[0];
        delete this.data.created_at;
        this.loadProviders();
        this.loadSec();
        this.loadProvidersItem();
        this.getVehiclesByPrefecture();
        if (this.data.nf_issue_date) {
          this.data.nf_issue_date = this.formatDateBr(this.data.nf_issue_date);
        }
        this.itens = JSON.parse(this.data.nf_item);
        this.$store.dispatch("setLoader", false);
      });
    }
    if (this.$store.getters.getUserData.table_key == 'prefectures') {
      this.formDisable = true;
      this.data.prefecture_id = this.$store.getters.getUserData.table_id;
      this.loadProviders();
      this.loadSec();
    }
  },
  methods: {
    onChangeParts(i) {
      if (this.itens[i].preco && this.itens[i].quantidade) {
        const value = parseFloat(this.itens[i].preco.split(".").join("").replace(",", ".")) * parseInt(this.itens[i].quantidade, 10);
        this.itens[i].total = value.toFixed(2).toString().replace('.', ',');
      }
    },
    onChangeTotal() {
      let total = 0;
      this.itens.map((item) => {
        if (item.isServico && item.preco) {
          total += parseFloat(item.preco.split(".").join("").replace(",", "."));
        } else if (item.total) {
          total += parseFloat(item.total.split(".").join("").replace(",", "."));
        }
      });
      if (this.data.nf_discount) {
        total -= parseFloat(this.data.nf_discount.split(".").join("").replace(",", "."));
      }
      this.data.nf_total = total.toFixed(2).toString().replace('.', ',');
    },
    getVehiclesByPrefecture() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/vehicles/prefectures/${this.data.prefecture_id}`)
        .then((v) => {
          this.vehicles = v.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.color = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    addFieldParts() {
      this.itens.push({
        peca_id: null,
        preco: "",
        quantidade: "",
        total: "",
        isServico: false,
      });
    },
    addFieldServices() {
      this.itens.push({
        servico: "",
        preco: "",
        isServico: true,
      });
    },
    removeField(index) {
      this.itens.splice(index, 1);
      this.onChangeTotal();
    },
    formatDateBr(date) {
      return date.substr(0, 10).split("-").reverse().join("/");
    },
    validate() {
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Atencão: um ou mais campos precisam ser preenchidos!";
      }
    },
    createOrUpdate() {
      this.loader = true;
      this.data.nf_item = JSON.stringify(this.itens);
      
      const prepareValue = (val) => {
        if (!val || val === "") return "0.00";
        return val.toString().split(".").join("").replace(",", ".");
      };

      this.data.nf_discount = prepareValue(this.data.nf_discount);
      this.data.nf_total = prepareValue(this.data.nf_total);

      this.$store.dispatch("setLoader", true);

      const request = this.id 
        ? this.$http.put(`/parts-and-services/nf/${this.id}`, this.data)
        : (this.data.itens = this.itens, this.$http.post("/parts-and-services/nf", this.data));

      request
        .then((r) => {
          this.snack.show = true;
          if (r.data.result || r.data.success) {
            this.snack.message = r.data.result || r.data.success;
            this.snack.color = "success";
            setTimeout(() => {
              this.$router.push("/parts-and-services/nf");
            }, 1500);
          } else {
            this.snack.message = r.data.error;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          }
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
    loadProviders() {
      this.$http
        .get(`/parts-and-services/providers/prefecture/${this.data.prefecture_id}`)
        .then((r) => (this.providers = r.data));
    },
    loadProvidersItem() {
      this.$http
        .get(`/parts-and-services/nf/providers/${this.data.provider_id}`)
        .then((r) => {
          if (r.data.length > 0) {
            this.providersItem = r.data;
            this.bidding_id = r.data[0].bidding_id;
          }
        });
    },
    loadSec() {
      this.$http
        .get(`/prefectures/secretariats/${this.data.prefecture_id}`)
        .then((r) => (this.prefectures_sec = r.data));
    },
  },
};
</script>

<style scoped>
.custom-select ::v-deep .v-input__slot {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 12px !important;
  height: 48px !important;
  padding: 0 16px !important;
}

.custom-select-small ::v-deep .v-input__slot {
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  height: 38px !important;
}

.custom-select-small-readonly ::v-deep .v-input__slot {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  height: 38px !important;
}

.custom-select ::v-deep input, .custom-select-small ::v-deep input {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #334155 !important;
  text-transform: uppercase !important;
}

.custom-select-small ::v-deep input {
  font-size: 12px !important;
}

.custom-select ::v-deep .v-label {
  font-size: 10px !important;
  font-weight: 900 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.15em !important;
  color: #94a3b8 !important;
}

/* Animations */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
